import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import useAdyenCheckout from '../hooks/useAdyenCheckout'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../../custom-api/layers/default'

export const getCheckoutConfig = (
    adyenEnvironment,
    adyenPaymentMethods,
    paymentMethodsConfiguration,
    translations,
    locale,
    adyenPaymentMethodsSubscriptions,
) => {
    const checkoutConfig = {
        environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
        clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
        paymentMethodsResponse: adyenPaymentMethodsSubscriptions || adyenPaymentMethods,
        paymentMethodsConfiguration: paymentMethodsConfiguration,
    }
    if (translations) {
        checkoutConfig.locale = locale.id
        checkoutConfig.translations = translations
    }
    return checkoutConfig
}

export const handleQueryParams = (
    urlParams,
    checkout,
    setAdyenPaymentInProgress,
    paymentContainer,
) => {
    const redirectResult = urlParams.get('redirectResult')
    const amazonCheckoutSessionId = urlParams.get('amazonCheckoutSessionId')
    const adyenAction = urlParams.get('adyenAction')

    if (redirectResult) {
        checkout.submitDetails({data: {details: {redirectResult}}})
    } else if (amazonCheckoutSessionId) {
        setAdyenPaymentInProgress(true)
        const amazonPayContainer = document.createElement('div')
        const amazonPay = checkout
            .create('amazonpay', {
                amazonCheckoutSessionId,
                showOrderButton: false,
            })
            .mount(amazonPayContainer)
        amazonPay.submit()
    } else if (adyenAction) {
        const actionString = atob(adyenAction)
        const action = JSON.parse(actionString)
        checkout.createFromAction(action).mount(paymentContainer.current)
    } else {
        checkout.create('dropin').mount(paymentContainer.current)
    }
}

const AdyenCheckoutComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        getPaymentMethodsConfiguration,
        setAdyenStateData,
        getTranslations,
        locale,
        adyenPaymentInProgress,
        setAdyenPaymentInProgress,
        navigate,
    } = useAdyenCheckout()
    const paymentContainer = useRef(null)
    const listenerAddedRef = useRef(false)
    const clickHandlerRef = useRef(null)
    const {addLayer} = useGoogleTagManager()

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search)

        const createCheckout = async () => {
            const paymentMethodsConfiguration = await getPaymentMethodsConfiguration(props)
            const translations = getTranslations()
            // Use filtered payment methods from props if provided
            const paymentMethodsToUse = props.paymentMethods || adyenPaymentMethods
            const checkoutConfig = getCheckoutConfig(
                adyenEnvironment,
                paymentMethodsToUse,
                paymentMethodsConfiguration,
                translations,
                locale,
                props.adyenPaymentMethodsSubscriptions,
            )
            const checkout = await AdyenCheckout({
                ...checkoutConfig,
                onSubmit(state, element) {
                    const onSubmit =
                        paymentMethodsConfiguration.onSubmit ||
                        paymentMethodsConfiguration.card.onSubmit
                    onSubmit(state, element)
                },
                onAdditionalDetails(state, element) {
                    const onAdditionalDetails =
                        paymentMethodsConfiguration.onAdditionalDetails ||
                        paymentMethodsConfiguration.card.onAdditionalDetails
                    onAdditionalDetails(state, element)
                },
                onChange: (state, element) => {
                    if (!clickHandlerRef.current) {
                        clickHandlerRef.current = async () => {
                            await addLayer(
                                basic_gtm_event.bind(this, {
                                    event: 'fillInErrors',
                                    eventCategory: 'ecommerce',
                                    eventAction: 'checkout',
                                    eventLabel: 'fillInErrors',
                                }),
                            )
                        }
                    }
                    const allPayButtons = element?._node?.querySelectorAll(
                        '.adyen-checkout__button.adyen-checkout__button--pay',
                    )
                    if (state.isValid) {
                        setAdyenStateData(state.data)
                        if (listenerAddedRef.current) {
                            allPayButtons.forEach((btn) =>
                                btn.removeEventListener('click', clickHandlerRef.current),
                            )
                            listenerAddedRef.current = false
                        }
                    } else {
                        allPayButtons.forEach((btn) =>
                            btn.removeEventListener('click', clickHandlerRef.current),
                        )
                        allPayButtons.forEach((btn) =>
                            btn.addEventListener('click', clickHandlerRef.current),
                        )
                        listenerAddedRef.current = true
                    }
                },
                onError: () => navigate('/checkout?error=true'),
            })
            handleQueryParams(urlParams, checkout, setAdyenPaymentInProgress, paymentContainer)
        }
        if (adyenEnvironment && paymentContainer.current && !adyenPaymentInProgress) {
            if (window?.paypal?.firstElementChild) {
                window.paypal = undefined
            }
            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods, props.paymentMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <div ref={paymentContainer}></div>
        </>
    )
}

AdyenCheckoutComponent.propTypes = {
    showLoading: PropTypes.bool,
    paymentMethods: PropTypes.any,
    adyenPaymentMethodsSubscriptions: PropTypes.any,
}

export default AdyenCheckoutComponent
